import supabase from "./supabase";

/**
 * Registers a new user account.
 *
 * @param {{ fullName: string, email: string, password: string }} credentials - Signup payload.
 * @returns {Promise<object>} Supabase signup response.
 */
export async function signup({ fullName, email, password }) {
    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
            data: {
                fullName,
                avatar: "",
            },
        },
    });

    if (error) throw new Error(error.message);
    return data;
}

/**
 * Signs a user in with email/password.
 *
 * @param {{ email: string, password: string }} credentials - Login payload.
 * @returns {Promise<object>} Supabase login response.
 */
export async function login({ email, password }) {
    let { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    if (error) throw new Error(error.message);
    return data;
}

/**
 * Returns the currently authenticated user.
 *
 * @returns {Promise<object | null>} Current user or null when no session exists.
 */
export async function getCurrentUser() {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return null;
    const { data, error } = await supabase.auth.getUser();
    if (error) throw new Error(error.message);
    return data?.user;
}

/**
 * Signs out the current user.
 *
 * @returns {Promise<void>} Resolves when logout succeeds.
 */
export async function logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
}

/**
 * Updates the current user's profile.
 *
 * @param {{ password?: string, fullName?: string }} updates - Fields to update.
 * @returns {Promise<object>} Updated user payload.
 */
export async function updateCurrentUser({ password, fullName }) {
    const updateData = {};
    if (password) updateData.password = password;
    if (fullName) updateData.data = { fullName };

    const { data, error } = await supabase.auth.updateUser(updateData);

    if (error) throw new Error(error.message);
    return data;
}

export async function checkUserIsAdmin(userId) {
    const { count, error } = await supabase
        .from("app_admins")
        .select("user_id", { count: "exact", head: true })
        .eq("user_id", userId);

    if (error) throw new Error(error.message);
    return (count ?? 0) > 0;
}
